/**
 * LMS Connector Integration (Phase 4 — Steps 4.6, 4.7).
 *
 * Canvas, Blackboard, Moodle attendance data pull and gradebook push.
 * Stub implementation — requires external API credentials for production use.
 */

/** Supported LMS providers */
export type LMSProvider = 'canvas' | 'blackboard' | 'moodle';

/** LMS connection configuration */
export interface LMSConfig {
  /** Which provider */
  provider: LMSProvider;
  /** OAuth access token */
  accessToken: string | null;
  /** LMS base URL — e.g., "https://university.instructure.com" */
  baseUrl: string | null;
  /** Course ID in the LMS */
  lmsCourseId: string | null;
}

/** An attendance record from the LMS */
export interface LMSAttendanceRecord {
  /** Student ID in LMS */
  studentId: string;
  /** ISO date */
  date: string;
  /** Attendance status from LMS */
  status: string;
}

/** LMS gradebook column */
export interface LMSGradeColumn {
  /** Column ID in LMS */
  id: string;
  /** Column title */
  title: string;
  /** Points possible */
  pointsPossible: number | null;
}

/** Result of pulling attendance data */
export interface LMSPullResult {
  /** Successfully imported records */
  records: LMSAttendanceRecord[];
  /** Error messages */
  errors: string[];
}

/** Result of pushing grades */
export interface LMSPushResult {
  /** Number of grades successfully pushed */
  pushed: number;
  /** Number of failures */
  failed: number;
  /** Error messages */
  errors: string[];
}

/** Default LMS config */
export const DEFAULT_LMS_CONFIG: LMSConfig = {
  provider: 'canvas',
  accessToken: null,
  baseUrl: null,
  lmsCourseId: null,
};

/**
 * Generate OAuth URL for LMS connection.
 */
export function getLMSAuthUrl(provider: LMSProvider): string {
  const clientId = import.meta.env.VITE_LMS_CLIENT_ID ?? 'stub-client-id';
  const redirectUri = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173';

  switch (provider) {
    case 'canvas':
      return `${DEFAULT_LMS_CONFIG.baseUrl ?? 'https://canvas.instructure.com'}/login/oauth2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code`;
    case 'blackboard':
      return `${DEFAULT_LMS_CONFIG.baseUrl ?? 'https://blackboard.com'}/learn/api/public/v1/oauth2/authorizationcode?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code`;
    case 'moodle':
      return `${DEFAULT_LMS_CONFIG.baseUrl ?? 'https://moodle.com'}/local/oauth/login.php?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code`;
  }
}

/**
 * Pull attendance data from the LMS.
 * Stub — in production, this would call the LMS API.
 */
export async function pullAttendanceData(
  _config: LMSConfig,
): Promise<LMSPullResult> {
  if (!_config.accessToken) {
    return { records: [], errors: ['Not authenticated. Please connect your LMS first.'] };
  }
  if (!_config.lmsCourseId) {
    return { records: [], errors: ['No LMS course selected.'] };
  }
  // Stub: would make API call to fetch attendance records
  return { records: [], errors: ['LMS integration not yet configured for this institution.'] };
}

/**
 * List available gradebook columns in the LMS.
 */
export async function listGradeColumns(
  _config: LMSConfig,
): Promise<LMSGradeColumn[]> {
  if (!_config.accessToken) return [];
  // Stub
  return [];
}

/**
 * Push attendance grade to the LMS gradebook.
 */
export async function pushGradeToLMS(
  _config: LMSConfig,
  _columnId: string,
  _grade: number,
  _comment?: string,
): Promise<LMSPushResult> {
  if (!_config.accessToken) {
    return { pushed: 0, failed: 1, errors: ['Not authenticated.'] };
  }
  if (!_config.lmsCourseId) {
    return { pushed: 0, failed: 1, errors: ['No LMS course selected.'] };
  }
  // Stub: would make API call to push grade
  return { pushed: 0, failed: 1, errors: ['LMS gradebook push not yet available.'] };
}
